// src/features/rooms/DeleteDevicesInSelectedRoomsModal.tsx
import React, { useMemo } from "react";
import { Alert, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { FaExclamationTriangle, FaTrash } from "react-icons/fa";
import { useAppSelector } from "../../../app/hooks";
import { selectDevicesInSelectedRooms } from "../deviceSelectors"; // ⚠️ juster path
import { usePostMultiplacerDeviceDeleteInRoomsMutation } from "../../../../openapi";

type Props = {
  opened: boolean;
  onClose: () => void;
};

export const DeleteDevicesInSelectedRoomsModal: React.FC<Props> = ({ opened, onClose }) => {
  const projectNo = useAppSelector((s) => s.design.projectNo);
  const selectedRooms = useAppSelector((s) => s.design.selectedRooms);
  const devicesInSelectedRooms = useAppSelector(selectDevicesInSelectedRooms);

  const roomCount = selectedRooms?.length ?? 0;
  const deviceCount = devicesInSelectedRooms?.length ?? 0;

  const title = useMemo(() => {
    if (deviceCount === 1) return "Slette 1 enhet?";
    return `Slette ${deviceCount} enheter?`;
  }, [deviceCount]);

  const [deleteDevicesInRooms, { isLoading, isSuccess, isError }] =
    usePostMultiplacerDeviceDeleteInRoomsMutation();

  // ✅ Lukk modalen automatisk når mutasjonen lykkes
  React.useEffect(() => {
    if (opened && isSuccess) {
      onClose();
    }
  }, [opened, isSuccess, onClose]);

  const canDelete = roomCount > 0 && deviceCount > 0;

  const handleConfirmDelete = () => {
    if (!canDelete) return;

    // Ingen unwrap / try-catch – toasts håndteres i onQueryStarted
    deleteDevicesInRooms({
      body: {
        projectNo,
        roomNumbers: selectedRooms,
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={isLoading ? () => {} : onClose}
      title={<Text fw={700}>{title}</Text>}
      centered
    >
      <Stack gap="sm">
        <Alert icon={<FaExclamationTriangle />} title="Dette kan ikke angres" color="red">
          Du er i ferd med å slette enheter i valgte rom. Dette går ikke å angre.
        </Alert>

        <Text size="sm">
          <b>Valgte rom:</b> {roomCount}
        </Text>

        <Text size="sm">
          <b>Komponenter som blir slettet:</b> {deviceCount}
        </Text>

        <Text size="sm" c="dimmed">
          Alle komponenter i de valgte rommene blir fjernet permanent.
        </Text>

        {/* Valgfritt: viser liten statuslinje i modalen, mens toasts tar seg av resten */}
        {isError && (
          <Alert color="red" title="Feil">
            Kunne ikke slette komponenter.
          </Alert>
        )}

        <Group justify="flex-end" mt="xs">
          <Button variant="neutral" onClick={onClose} disabled={isLoading}>
            Avbryt
          </Button>

          <Button
            color="red"
            leftSection={<FaTrash size={14} />}
            onClick={handleConfirmDelete}
            loading={isLoading}
            disabled={!canDelete}
          >
            Slett
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
